/**
 * The subtitle height of a format (1.7.1, moved here from the Format dialog): how far above the bottom the subtitle line
 * sits, as a percentage of the frame height (2–50 %); left out is the default (captionLayout: 20 % for portrait, 7.5 %
 * for landscape and square). The Format dialog shows the compact field, the Subtitles tab's Position section a row with
 * a slider, a number field and Default. Every change goes through ops.updateFormat with one coalesce key per format
 * (`subtitle-bottom:<id>`, shared with dragging the line in the monitor), so a slider drag or a typed value is one undo step.
 */
import { useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { CAPTION, ops, type FormatRef, type ResolvedFormat } from '@prism/contracts';
import type { EditorApply } from './formatContext';
import { formatColor } from './formatContext';

/** The default subtitle height of a format's shape (a fraction of the height). */
export const subtitleDefault = (f: { width: number; height: number }): number => (f.height > f.width ? CAPTION.bottomPortrait : CAPTION.bottomLandscape);

/** The undo coalescing key of a format's subtitle height. */
export const subtitleCoalesce = (id: FormatRef): string => `subtitle-bottom:${id}`;

/** A height as a percentage with at most one decimal ("7.5"). */
export const subtitlePct = (v: number): string => String(Math.round(v * 1000) / 10);

const num = (v: string) => (v.trim() === '' ? NaN : Number(v));
const clampPct = (v: number) => Math.min(50, Math.max(2, v));

/** Set (a percentage) or clear (null) a format's subtitle height. */
function useSubtitleHeight(f: ResolvedFormat, apply: EditorApply, seal?: () => void) {
  const set = (pct: number | null) => apply((d) => ops.updateFormat(d, f.id, { subtitleBottom: pct === null ? null : clampPct(pct) / 100 }), subtitleCoalesce(f.id));
  const [draft, setDraft] = useState<string | null>(null);
  /** The number field: typed freely, applied when left (empty is the default). */
  const field = {
    value: draft ?? (f.subtitleBottom !== undefined ? subtitlePct(f.subtitleBottom) : ''),
    onChange: (v: string) => setDraft(v),
    onBlur: () => {
      if (draft === null) return;
      const v = num(draft);
      setDraft(null);
      if (draft.trim() === '') { if (f.subtitleBottom !== undefined) set(null); }
      else if (Number.isFinite(v)) set(v);
      seal?.();
    },
  };
  return { def: subtitleDefault(f), set, field };
}

/** The Format dialog's field: a percentage, empty for the default. */
export function SubtitleHeightField({ f, apply, seal, disabled }: { f: ResolvedFormat; apply: EditorApply; seal?: () => void; disabled?: boolean }) {
  const h = useSubtitleHeight(f, apply, seal);
  return (
    <label className="format-subs" title="How far above the bottom subtitles sit, as a percentage of the height (2–50 %)">
      <span className="faint">Subtitles</span>
      <input className="input mono" inputMode="decimal" value={h.field.value} placeholder={subtitlePct(h.def)} disabled={disabled} aria-label={`Subtitle height of ${f.label} (% from the bottom)`}
        data-testid={`format-subs-${f.id}`} onChange={(e) => h.field.onChange(e.target.value)} onBlur={h.field.onBlur} />
      <span className="faint">%</span>
    </label>
  );
}

/** The Subtitles tab's row of a format: its chip, a slider and a number field (2–50 %), and Default. */
export function SubtitleHeightRow({ f, apply, seal, disabled, showChip }: { f: ResolvedFormat; apply: EditorApply; seal: () => void; disabled?: boolean; showChip: boolean }) {
  const h = useSubtitleHeight(f, apply, seal);
  const value = f.subtitleBottom ?? h.def;
  return (
    <div className="subs-height-row" data-testid={`subs-height-${f.id}`} data-value={subtitlePct(value)}>
      {showChip && <span className="subs-height-format"><span className="format-swatch" style={{ background: formatColor(f.index) }} aria-hidden />{f.label}{f.isPrimary ? <span className="faint"> primary</span> : null}</span>}
      <label className="faint subs-height-label" htmlFor={`subs-height-range-${f.id}`}>Height above the bottom</label>
      <input id={`subs-height-range-${f.id}`} type="range" min={2} max={50} step={0.5} value={value * 100} disabled={disabled} data-testid={`subs-height-range-${f.id}`}
        aria-label={`Subtitle height of ${f.label} (% from the bottom)`}
        onChange={(e) => h.set(Number(e.target.value))} onPointerUp={seal} onKeyUp={seal} />
      <input className="input mono subs-height-num" inputMode="decimal" value={h.field.value} placeholder={subtitlePct(h.def)} disabled={disabled} data-testid={`subs-height-num-${f.id}`}
        aria-label={`Subtitle height of ${f.label} in %`} onChange={(e) => h.field.onChange(e.target.value)} onBlur={h.field.onBlur}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
      <span className="faint">%</span>
      <button type="button" className="btn ghost sm" disabled={disabled || f.subtitleBottom === undefined} onClick={() => { h.set(null); seal(); }} data-testid={`subs-height-default-${f.id}`}
        title={`Back to the default for this shape (${subtitlePct(h.def)} %)`}><RotateCcw size={12} /> Default</button>
    </div>
  );
}
